import React, { useEffect, useState } from 'react';
import {
  TextField,
  Grid,
  Button,
  Typography,
  Collapse,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableFooter,
  TablePagination,
  InputAdornment,
  Chip,
  Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ClearIcon from '@mui/icons-material/Clear';
import { HealthSystemDomainFeedFeedSuppliersFeedSupplierViewModel } from '../../../api/HealthSystemApi';
import { ApiService } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE_MIDDLE } from '../../../constants/constants';

interface Props {
  onSelect: (supplier: HealthSystemDomainFeedFeedSuppliersFeedSupplierViewModel) => void;
}

const FeedSupplierAutocomplete: React.FC<Props> = ({ onSelect }) => {
  const [name, setName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [country, setCountry] = useState('Netherlands');
  const [searchResult, setSearchResult] = useState<HealthSystemDomainFeedFeedSuppliersFeedSupplierViewModel[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [page, setPage] = useState(1);
  const apiService = new ApiService();
  const [total, setTotal] = React.useState(0);
  const { t } = useTranslation();
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE_MIDDLE);
  const [addedSuppliers, setAddedSuppliers] = useState<HealthSystemDomainFeedFeedSuppliersFeedSupplierViewModel[]>([]);

  const fetchSuppliers = async (pageNumber = 1) => {
    const response = await apiService.api.feedSupplier.feedSupplierGetFeedSuppliers({
      Name: name,
      RegistrationNumber: registrationNumber,
      Country: country,
      Page: pageNumber,
      Size: 10
    });

    setTotal(response.data.total);
    setSearchResult(response.data.data ?? []);
    setShowResults(true);
  };

  useEffect(() => {
    fetchSuppliers(page);
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    fetchSuppliers(1);
  };

  const handleClearFilters = () => {
    setName('');
    setRegistrationNumber('');
    setCountry('');
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            slotProps={{
              input: {
                endAdornment: name && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setName('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            size="small"
            label="Registration Number"
            value={registrationNumber}
            onChange={(e) => setRegistrationNumber(e.target.value)}
            slotProps={{
              input: {
                endAdornment: registrationNumber && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setRegistrationNumber('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            size="small"
            label="Country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            slotProps={{
              input: {
                endAdornment: country && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setCountry('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Button variant="contained" onClick={handleSearch} fullWidth>
            Search
          </Button>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Button variant="outlined" onClick={handleClearFilters} fullWidth>
            Clear Filters
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ gap: 1, alignItems: 'center', display: 'flex' }}>
            <Typography>{t('f-search-results')}</Typography>
            <Chip label={total} variant="outlined" id="totalChip" />
            <IconButton onClick={() => setShowResults(!showResults)} size="small">
              {showResults ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      <Collapse in={showResults} timeout="auto" unmountOnExit>
        <Grid size={{ xs: 12 }}>
          <Table size="small" id="animalSearchResultTable">
            <TableHead>
              <TableRow>
                <TableCell>{t('f-pre-delivery-company-name')}</TableCell>
                <TableCell>{t('f-pre-delivery-ubn')}</TableCell>
                <TableCell>{t('f-country')}</TableCell>
                <TableCell>{t('f-pre-delivery-address')}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            {searchResult.length > 0 && (
              <>
                <TableBody>
                  {searchResult.map((supplier) => (
                    <TableRow>
                      <TableCell>{supplier.companyName}</TableCell>
                      <TableCell>{supplier.registrationNumber}</TableCell>
                      <TableCell>{supplier.country}</TableCell>
                      <TableCell>{supplier.companyAddress}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => {
                            onSelect(supplier);
                            setAddedSuppliers((prev) => [...prev, supplier]);
                          }}
                          disabled={addedSuppliers.some((s) => s.registrationNumber === supplier.registrationNumber)}
                        >
                          {t('f-pre-delivery-add-feed-supplier')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                      count={total}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </>
            )}
          </Table>
        </Grid>
      </Collapse>
    </Paper>
  );
};

export default FeedSupplierAutocomplete;
